import React, {FC, ReactElement, ReactNode} from "react";
import {Card, Grid, Step, StepButton, StepLabel, Stepper, Theme, Typography} from "@mui/material";

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

export type StepperStep = {
    label: string,
    content: (params: {
        completeCurrent: () => void,
        navigateToNext: () => void,
        goBack: () => void,
    }) => ReactNode,
    hasError?: () => boolean
    disabled?: () => boolean
    isComplete: boolean
}

interface IProps {
    initialStep?: number
    steps: StepperStep[]
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            height: "100%"
        },
        button: {
            marginRight: theme.spacing(1),
        },
        backButton: {
            marginRight: theme.spacing(1),
        },
        completed: {
            display: 'inline-block',
        },
        instructions: {
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
    }),
);

export const HorizontalStepper: FC<IProps> = ({steps, initialStep}): ReactElement => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = React.useState(initialStep ?? 0);

    const totalSteps = () => {
        return steps.length;
    };

    const completedSteps = () => {
        return steps.filter(it => it.isComplete).length;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && !allStepsCompleted()
                ? // It's the last step, but not all steps have been completed
                  // find the first step that has been completed
                steps.findIndex(it => !it.isComplete)
                : activeStep + 1;

        setActiveStep(newActiveStep);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStep = (step: number) => () => {
        setActiveStep(step);
    };

    const handleComplete = () => {
        if (completedSteps() !== totalSteps()) {
            handleNext();
        }
    };


    function isStepComplete(step: number) {
        return steps[step].isComplete;
    }

    return (
        <Card className={classes.root}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Stepper alternativeLabel nonLinear activeStep={activeStep}>
                        {steps.map(({label, hasError, disabled}, index) => {
                            const stepProps: { completed?: boolean } = {};
                            const buttonProps: { optional?: React.ReactNode } = {};
                            return (
                                <Step key={label} {...stepProps} completed={isStepComplete(index)}>
                                    <StepLabel error={hasError ? hasError() : false}>
                                        <StepButton onClick={handleStep(index)} disabled={disabled ? disabled() : false} {...buttonProps}>
                                            {activeStep === index ? <Typography variant={"h6"}><u>{label}</u></Typography> : label}
                                        </StepButton>
                                    </StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                </Grid>
                <Grid item xs={12}>
                    <Card variant={"elevation"} elevation={3} className={classes.root}>
                        {steps[activeStep].content({completeCurrent: handleComplete, navigateToNext: handleNext, goBack: handleBack})}
                    </Card>
                </Grid>
            </Grid>
        </Card>
    );
};
